import { customToast } from "@/components/Common/Toast";
import {
  addEmployeeContributionAPI,
  fetchEmployeeContributionData,
} from "@/services/contributions";
import { RootState } from "@/store";
import { incrementCurrentContributionStep } from "@/store/wizard";
import { EmployeeTokenData } from "@/types/contributions";
import { useContributionCalculations } from "@/utils/contribution-calculator";
import { handleApiError } from "@/utils/errorHandling";
import { getDecodedTokenSafe } from "@/utils/tokenUtils";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { UserBenefitData } from "../../Introduction/types";
import {
  EmployeeContributionData,
  EmployeeContributionFormData,
} from "../types/contributionTypes";
import { getWizardConfig } from "@/config/wizardConfig";
import {
  INITIAL_EMPLOYEE_DATA,
  calculateEmployerMatch,
  formatContributionAmount,
} from "../utils/contributionUtils";
import { validateContributionReadiness } from "../utils/contributionValidation";

interface UseEmployeeContributionReturn extends EmployeeContributionFormData {
  loading: boolean;
  isSubmitting: boolean;
  error: string | null;
  handleContributionChange: (name: string, value: number) => void;
  handleSubmitContribution: () => Promise<void>;
  initializeData: () => Promise<void>;
}

export const useEmployeeContribution = (
  isUserEnrolled?: boolean,
): UseEmployeeContributionReturn => {
  const dispatch = useDispatch();
  const { id: useraccountid, employeeID } = useSelector(
    (state: RootState) => state.user,
  );

  const { calculateForPayPeriod } = useContributionCalculations();

  const [loading, setLoading] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [employeeContribution, setEmployeeContribution] = useState<number>(0);
  const [employerContribution, setEmployerContribution] = useState<number>(0);
  const [employeeInformation, setEmployeeInformation] =
    useState<EmployeeContributionData>(INITIAL_EMPLOYEE_DATA);
  const [benefitContributionLimit, setBenefitContributionLimit] =
    useState<string>("0");

  const [perPaycheckEmployeeDeduction, setPerPaycheckEmployeeDeduction] =
    useState<number>(0);
  const [perPaycheckEmployerContribution, setPerPaycheckEmployerContribution] =
    useState<number>(0);

  const { EmployerMaxMatch, EmployeeSalary } = employeeInformation;

  // Skip employee contribution step if user is already enrolled
  useEffect(() => {
    if (isUserEnrolled) {
      dispatch(incrementCurrentContributionStep());
    } else {
      initializeData();
    }
  }, [isUserEnrolled, dispatch]);

  // Recalculate contributions when employee contribution changes
  useEffect(() => {
    if (EmployeeSalary > 0) {
      const calculations = calculateForPayPeriod({
        annualSalary: EmployeeSalary,
        employeeContributionPct: employeeContribution,
        employerMaxMatchPct: EmployerMaxMatch,
        payPeriods: getWizardConfig().contributions.paychecksPerYear,
      });

      // Cap employer matching by benefit contribution limit (per-paycheck limit)
      const benefitLimit = Number(benefitContributionLimit);
      const cappedEmployerContribution =
        benefitLimit > 0
          ? Math.min(calculations.employerContribution, benefitLimit)
          : calculations.employerContribution;

      setPerPaycheckEmployeeDeduction(
        formatContributionAmount(calculations.employeeContribution),
      );
      setPerPaycheckEmployerContribution(
        formatContributionAmount(cappedEmployerContribution),
      );
    }
  }, [
    employeeContribution,
    EmployeeSalary,
    EmployerMaxMatch,
    calculateForPayPeriod,
    benefitContributionLimit,
  ]);

  const fetchEmployeeAndBenefitData = useCallback(() => {
    const decodedToken = getDecodedTokenSafe<
      EmployeeTokenData & UserBenefitData
    >(() => {
      setError("Unable to fetch employee data");
    });

    if (decodedToken) {
      setEmployeeInformation({
        UserAccountID: decodedToken.UserAccountID,
        EmployeeID: decodedToken.EmployeeID,
        FirstName: decodedToken.FirstName,
        LastName: decodedToken.LastName,
        EmployerMaxMatch: decodedToken.EmployerMaxContribution,
        EmployeeSalary: decodedToken.EmployeeSalary,
        EmployerMaxAnnualContribution:
          decodedToken.EmployeeSalary *
          (decodedToken.EmployerMaxContribution / 100),
      });

      if (decodedToken.BenefitContributionLimit) {
        setBenefitContributionLimit(decodedToken.BenefitContributionLimit);
      }
    }
  }, []);

  const initializeData = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);

      await fetchEmployeeAndBenefitData();

      const response = await fetchEmployeeContributionData(
        employeeID,
        parseInt(useraccountid),
      );

      if (response.data) {
        const {
          employeeTotalContribution,
          employeeTotalContributionInDollar,
          employerTotalContribution,
          employerTotalContributionInDollar,
        } = response.data;

        if (employeeTotalContribution) {
          setEmployeeContribution(employeeTotalContribution);
        }
        if (employerTotalContribution) {
          setEmployerContribution(employerTotalContribution);
        }
        if (employeeTotalContributionInDollar) {
          setPerPaycheckEmployeeDeduction(employeeTotalContributionInDollar);
        }
        if (employerTotalContributionInDollar) {
          setPerPaycheckEmployerContribution(employerTotalContributionInDollar);
        }
      }
    } catch {
      setError("Unable to fetch employee contribution data");
    } finally {
      setLoading(false);
    }
  }, [employeeID, useraccountid, fetchEmployeeAndBenefitData]);

  const handleContributionChange = useCallback(
    (name: string, value: number) => {
      if (name === "employeeContribution") {
        setEmployeeContribution(value);
        const employerMatchPct = calculateEmployerMatch(
          value,
          EmployerMaxMatch,
        );
        setEmployerContribution(employerMatchPct);
      }
    },
    [EmployerMaxMatch],
  );

  const handleSubmitContribution = useCallback(async () => {
    if (error) {
      customToast("Unable to continue.", "error");
      return;
    }

    const validation = validateContributionReadiness(
      employeeContribution,
      perPaycheckEmployeeDeduction,
    );

    if (!validation.isValid) {
      customToast(validation.errors[0], "error");
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        userAccountID: +employeeInformation.UserAccountID,
        employeeID: employeeInformation.EmployeeID,
        employeeTotalContribution: employeeContribution,
        employeeTotalContributionInDollar: Math.round(
          perPaycheckEmployeeDeduction,
        ),
        employerTotalContribution: employerContribution,
        employerTotalContributionInDollar: Math.round(
          perPaycheckEmployerContribution,
        ),
        createdUserAccountID: +employeeInformation.UserAccountID,
        sourceProcess: "WebApplication",
        changeReason: "Adding employee contribution",
      };

      await addEmployeeContributionAPI(payload);

      customToast("Your contribution has been successfully set", "success");
      dispatch(incrementCurrentContributionStep());
    } catch (error) {
      handleApiError(error, "Failed to save contribution", true, customToast);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    error,
    employeeContribution,
    perPaycheckEmployeeDeduction,
    employeeInformation,
    employerContribution,
    perPaycheckEmployerContribution,
    dispatch,
  ]);

  const calculations = {
    employeeContribution,
    employerContribution,
    perPaycheckEmployeeDeduction,
    perPaycheckEmployerContribution,
    totalPerPaycheckContribution:
      perPaycheckEmployeeDeduction + perPaycheckEmployerContribution,
  };

  return {
    loading,
    isSubmitting,
    error,
    employeeContribution,
    employerContribution,
    employeeInformation,
    benefitContributionLimit,
    calculations,
    handleContributionChange,
    handleSubmitContribution,
    initializeData,
  };
};
